// Lightweight per-page SEO without react-helmet: sets title, description,
// canonical and optional JSON-LD. Call inside useEffect; returns cleanup.
const SITE = "https://crypt.pe";

// Prerender bakes per-route JSON-LD into the static shell (data-pr-ld).
// When the client injects its own copy after hydration, drop any baked
// block sharing an @type so crawlers that run JS never see duplicates.
const ldTypes = (parsed) => {
  const blocks = Array.isArray(parsed) ? parsed : [parsed];
  return blocks.flatMap((x) =>
    x && x["@graph"] ? x["@graph"].map((g) => g && g["@type"]) : [x && x["@type"]]);
};

export function removePrerenderLd(types) {
  document.head.querySelectorAll('script[type="application/ld+json"][data-pr-ld]').forEach((el) => {
    try {
      if (ldTypes(JSON.parse(el.textContent)).some((t) => types.has(t))) el.remove();
    } catch {
      el.remove();
    }
  });
}

// Meta length guards — search snippets truncate past ~60/160 chars.
export const clampMetaTitle = (t) => {
  if (!t || t.length <= 65) return t;
  const brandMatch = t.match(/\s*[|·]\s*crypt\.pe\s*$/i);
  const brand = brandMatch ? brandMatch[0] : "";
  let core = brand ? t.slice(0, t.length - brand.length) : t;
  const budget = Math.max(65 - brand.length, 30);
  if (core.length > budget) {
    const cut = core.slice(0, budget);
    const sep = Math.max(cut.lastIndexOf(" — "), cut.lastIndexOf(": "), cut.lastIndexOf(" · "), cut.lastIndexOf(" | "));
    core = (sep >= 22 ? cut.slice(0, sep) : cut.slice(0, cut.lastIndexOf(" "))).replace(/[\s,;:—·|-]+$/, "");
  }
  return `${core}${brand}`;
};

export const clampMetaDescription = (d) => {
  if (!d || d.length <= 160) return d;
  const cut = d.slice(0, 158);
  const sentence = cut.lastIndexOf(". ");
  if (sentence >= 90) return cut.slice(0, sentence + 1);
  const word = cut.lastIndexOf(" ");
  return `${cut.slice(0, word > 0 ? word : 157).replace(/[\s,;:—·-]+$/, "").replace(/\.$/, "")}.`;
};

export function setPageMeta({ title, description, path, jsonLd }) {
  const prevTitle = document.title;
  if (title) document.title = clampMetaTitle(title);

  let desc = document.querySelector('meta[name="description"]');
  const prevDesc = desc?.getAttribute("content");
  if (description) {
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", clampMetaDescription(description));
  }

  let canon = document.querySelector('link[rel="canonical"]');
  const prevCanon = canon?.getAttribute("href");
  if (path) {
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `${SITE}${path}`);
  }

  let ld = null;
  if (jsonLd) {
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const types = new Set(blocks.flatMap((x) =>
      x && x["@graph"] ? x["@graph"].map((g) => g && g["@type"]) : [x && x["@type"]]));
    removePrerenderLd(types);
    ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify(jsonLd);
    document.head.appendChild(ld);
  }

  return () => {
    document.title = prevTitle;
    if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    if (canon && prevCanon != null) canon.setAttribute("href", prevCanon);
    if (ld) ld.remove();
  };
}
